import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { reportError } from "@/lib/errorReporter";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      console.error("[ErrorBoundary]", error, info);
    }
    reportError("react_error", error, { componentStack: info.componentStack ?? undefined });
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error } = this.state;
    const isDev = import.meta.env.DEV;

    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-primary p-6">
        <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-8 shadow-elevated">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-700">
              <AlertTriangle className="h-6 w-6" aria-hidden="true" />
            </div>
            <h1 className="mt-4 text-xl font-semibold text-ink-primary">
              Algo salió mal
            </h1>
            <p className="mt-2 text-sm text-ink-secondary">
              Ocurrió un error inesperado al cargar la aplicación. Puedes
              intentar nuevamente o recargar la página.
            </p>

            {isDev && error ? (
              <pre className="mt-6 max-h-64 w-full overflow-auto rounded-md border border-slate-200 bg-red-50 p-4 text-left text-xs text-red-700">
                <strong className="block font-semibold">{error.name}</strong>
                <span className="block">{error.message}</span>
                {error.stack ? (
                  <span className="mt-2 block whitespace-pre-wrap text-slate-700">
                    {error.stack}
                  </span>
                ) : null}
              </pre>
            ) : null}

            <div className="mt-6 flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
              <Button variant="primary" onClick={this.handleReset}>
                Intentar de nuevo
              </Button>
              <Button variant="secondary" onClick={this.handleReload}>
                Recargar página
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
